import * as THREE from "three";
import { TREES, VARS } from "../Global/variables";
import { pushUndoAction } from "../Global/UndoManager";
import { notifyIrradianceNeedsRecompute } from "../Shading/IrradianceEvents";
import { toast } from "../../lib/toast";
import { createTreeMesh, NATURAL_TOTAL_HEIGHT, setTreeHeightScale } from "./treeGeometry";

let activeTreeInteractionCleanup = null;

// Placement and interaction intentionally keep every tree on the application's
// world ground plane.  They otherwise use the sister application's authored,
// real-world geometry and world-space height interaction.
export function enableTreeDrawing(scene, camera, renderer, targetMeshes, setTrees, shape = "circle", options = {}) {
  activeTreeInteractionCleanup?.();
  const raycaster = new THREE.Raycaster();
  const mouse = new THREE.Vector2();
  let active = true;
  let drawingEnabled = options.drawingEnabled !== false;
  let placing = false;
  let preview = null;
  let startPoint = null;
  let selectedTree = null;
  let draggingTree = false;
  let draggingHeight = false;
  let dragOffset = new THREE.Vector3();
  let beforeDrag = null;

  const canvas = renderer.domElement;
  const targets = () => Array.isArray(targetMeshes) ? targetMeshes.filter(Boolean) : [targetMeshes].filter(Boolean);
  const isBusy = () => typeof window !== "undefined" && window.isIrradianceBusy === true;
  const isUiActive = () => typeof window === "undefined" || window.isTreeUiActive === true;
  const setCursor = (value) => { canvas.style.cursor = value; };
  const toNdc = (event) => {
    const rect = canvas.getBoundingClientRect();
    mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
    mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
    raycaster.setFromCamera(mouse, camera);
  };
  const rootFrom = (object) => {
    let root = object;
    while (root && !TREES.trees.includes(root)) root = root.parent;
    return root || null;
  };
  const capture = (tree) => tree ? {
    position: tree.position.clone(), scale: tree.scale.clone(), groundZ: tree.userData.groundZ,
    widthScale: tree.userData.widthScale, lengthScale: tree.userData.lengthScale, heightScaleY: tree.userData.heightScaleY,
    trunkScale: tree.userData.trunk.scale.clone(), trunkPosition: tree.userData.trunk.position.clone(),
    foliageScale: tree.userData.foliage.scale.clone(), foliagePosition: tree.userData.foliage.position.clone(),
    arrowScale: tree.userData.arrow.scale.clone(), arrowPosition: tree.userData.arrow.position.clone(),
  } : null;
  const restore = (tree, state) => {
    if (!tree || !state) return;
    tree.position.copy(state.position); tree.position.z = 0; tree.scale.copy(state.scale);
    Object.assign(tree.userData, { groundZ: 0, widthScale: state.widthScale, lengthScale: state.lengthScale, heightScaleY: state.heightScaleY });
    tree.userData.trunk.scale.copy(state.trunkScale); tree.userData.trunk.position.copy(state.trunkPosition);
    tree.userData.foliage.scale.copy(state.foliageScale); tree.userData.foliage.position.copy(state.foliagePosition);
    tree.userData.arrow.scale.copy(state.arrowScale); tree.userData.arrow.position.copy(state.arrowPosition);
    tree.updateMatrixWorld(true); setTrees([...(TREES.trees || [])]);
  };
  const changed = (a, b) => JSON.stringify(a) !== JSON.stringify(b);
  const invalidate = (reason) => notifyIrradianceNeedsRecompute(reason);
  const highlight = (tree, on) => {
    tree?.traverse((obj) => { if (obj.material?.emissive) obj.material.emissive.set(on ? 0x444400 : 0x000000); });
  };
  const emitSelected = (tree) => {
    // Tree movement is handled by our direct drag interaction.  Detach any
    // legacy TransformControls so the RGB axis gizmo can never remain visible.
    if (window.transformControls?.object) window.transformControls.detach?.();
    if (selectedTree && selectedTree !== tree) { highlight(selectedTree, false); selectedTree.userData.arrow.visible = false; }
    selectedTree = tree || null;
    if (selectedTree) { highlight(selectedTree, true); selectedTree.userData.arrow.visible = !selectedTree.userData.locked; }
    VARS.selectedTree = selectedTree;
    window.dispatchEvent(new CustomEvent("TREE_SELECTED", { detail: { tree: selectedTree } }));
  };
  const commit = (label, before, tree, reason) => {
    const after = capture(tree);
    if (!changed(before, after)) return;
    pushUndoAction({ label, undo: () => { restore(tree, before); invalidate(reason); }, redo: () => { restore(tree, after); invalidate(reason); } });
    invalidate(reason);
  };
  const groundHit = (event) => {
    toNdc(event);
    return raycaster.intersectObjects(targets(), true)[0] || null;
  };
  const hitTree = (event, arrowsOnly = false) => {
    toNdc(event);
    const hits = raycaster.intersectObjects(TREES.trees, true);
    const hit = arrowsOnly ? hits.find((entry) => entry.object.name === "heightArrow") : hits[0];
    return hit ? { hit, tree: rootFrom(hit.object) } : null;
  };
  const startMove = (tree, point) => {
    if (!tree || tree.userData.locked || isBusy() || !isUiActive()) return;
    draggingTree = true; beforeDrag = capture(tree); dragOffset.copy(point).sub(tree.position); setCursor("grabbing");
  };
  const updateMove = (event) => {
    if (!draggingTree || !selectedTree) return;
    const hit = groundHit(event); if (!hit) return;
    selectedTree.position.set(hit.point.x - dragOffset.x, hit.point.y - dragOffset.y, 0);
    selectedTree.userData.groundZ = 0; selectedTree.updateMatrixWorld(true);
  };
  const projectHeight = (event, tree) => {
    toNdc(event);
    const normal = new THREE.Vector3(); camera.getWorldDirection(normal);
    normal.z = 0;
    if (normal.lengthSq() < 1e-6) normal.set(0, 1, 0); else normal.normalize();
    const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(normal, tree.position);
    const point = new THREE.Vector3();
    return raycaster.ray.intersectPlane(plane, point) ? point.z : null;
  };
  const startHeight = (tree, event) => {
    if (!tree || tree.userData.locked || isBusy() || !isUiActive()) return;
    draggingHeight = true; beforeDrag = capture(tree); setCursor("ns-resize"); event.preventDefault();
  };
  const updateHeight = (event) => {
    if (!draggingHeight || !selectedTree) return;
    const z = projectHeight(event, selectedTree); if (!Number.isFinite(z)) return;
    const height = Math.max(0.15, z / NATURAL_TOTAL_HEIGHT);
    setTreeHeightScale(selectedTree, height);
    window.dispatchEvent(new CustomEvent("TREE_GEOMETRY_LIVE", { detail: { tree: selectedTree } }));
  };
  const stopInteraction = () => {
    if (draggingTree && selectedTree) commit("Move tree", beforeDrag, selectedTree, "tree-moved");
    if (draggingHeight && selectedTree) commit("Resize tree", beforeDrag, selectedTree, "tree-resized");
    draggingTree = false;
    draggingHeight = false;
    beforeDrag = null;
    // A simple click on a selected tree enters and immediately exits the drag
    // path. Do not flash the placement cursor after drawing has completed.
    setCursor(active && drawingEnabled ? "crosshair" : "");
  };
  const finishPlacement = () => {
    if (!preview) return;
    preview.traverse((obj) => { if (obj.material) { obj.material.wireframe = false; obj.material.transparent = false; obj.material.opacity = 1; } });
    preview.position.z = 0; preview.userData.groundZ = 0;
    TREES.trees.push(preview); setTrees([...(TREES.trees || [])]);
    const created = preview; preview = null; startPoint = null; placing = false;
    pushUndoAction({ label: "Create tree", undo: () => { const i = TREES.trees.indexOf(created); if (i >= 0) TREES.trees.splice(i, 1); created.parent?.remove(created); if (VARS.selectedTree === created) emitSelected(null); setTrees([...(TREES.trees || [])]); invalidate("tree-created"); }, redo: () => { if (!created.parent) scene.add(created); if (!TREES.trees.includes(created)) TREES.trees.push(created); setTrees([...(TREES.trees || [])]); invalidate("tree-created"); } });
    invalidate("tree-created"); toast.success("Tree added"); emitSelected(created);
    // Keep selection/move/height interactions alive after the one tree has been
    // placed, but make the drawing action one-shot like the sister tool.
    drawingEnabled = false;
    setCursor("");
    window.__setCanvasNotice?.((previous) => previous?.source === "tree-drawing"
      ? { ...previous, visible: false, source: null }
      : previous);
  };
  const onDown = (event) => {
    if (!active || event.button !== 0) return;
    const arrow = hitTree(event, true);
    if (arrow && arrow.tree && arrow.tree === VARS.selectedTree) { startHeight(arrow.tree, event); return; }
    const selected = hitTree(event);
    if (selected && selected.tree) { emitSelected(selected.tree); if (!selected.tree.userData.locked) startMove(selected.tree, selected.hit.point); return; }
    if (!drawingEnabled || placing) {
      if (!placing) emitSelected(null);
      return;
    }
    const hit = groundHit(event); if (!hit || isBusy()) return;
    placing = true; startPoint = new THREE.Vector3(hit.point.x, hit.point.y, 0); preview = createTreeMesh(shape);
    preview.userData.__preview = true; preview.position.copy(startPoint); preview.traverse((obj) => { if (obj.material) { obj.material.wireframe = true; obj.material.transparent = true; obj.material.opacity = 0.65; } }); scene.add(preview);
  };
  const onMove = (event) => {
    if (!active) return;
    if (draggingTree) return updateMove(event);
    if (draggingHeight) return updateHeight(event);
    if (placing && preview) { const hit = groundHit(event); if (!hit) return; const dx = hit.point.x - startPoint.x; const dy = hit.point.y - startPoint.y; const width = Math.max(0.15, Math.hypot(dx, dy)); preview.userData.widthScale = width; preview.userData.lengthScale = width; setTreeHeightScale(preview, width); preview.position.z = 0; return; }
    const hit = hitTree(event);
    setCursor(hit && hit.tree && !hit.tree.userData.locked && isUiActive() ? "move" : drawingEnabled ? "crosshair" : "");
  };
  const onUp = () => { if (placing) finishPlacement(); else stopInteraction(); };
  const onKeyDown = (event) => { if (event.key === "Escape" && drawingEnabled) cleanup(); };
  const cleanup = () => {
    if (!active) return; active = false; canvas.removeEventListener("pointerdown", onDown); canvas.removeEventListener("pointermove", onMove); window.removeEventListener("pointerup", onUp); window.removeEventListener("keydown", onKeyDown); canvas.style.cursor = "";
    if (preview) { preview.parent?.remove(preview); preview = null; }
    window.__setCanvasNotice?.((previous) => previous?.source === "tree-drawing" ? { ...previous, visible: false, source: null } : previous);
  };
  canvas.addEventListener("pointerdown", onDown); canvas.addEventListener("pointermove", onMove); window.addEventListener("pointerup", onUp); window.addEventListener("keydown", onKeyDown);
  setCursor("crosshair");
  if (drawingEnabled) {
    window.__setCanvasNotice?.({ visible: true, intent: "info", source: "tree-drawing", cancelable: true, iconName: "tree", title: "Drawing a tree", subtitle: "Click and drag to set the tree footprint. Press Esc to cancel." });
  }
  activeTreeInteractionCleanup = cleanup;
  return cleanup;
}
